/**
 * Copyright IBM Corp. 2022, 2026
 */

import { Link } from 'lib/i18n/routing'
import classNames from 'classnames'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import {
	Text,
	DisclosurePrimitive,
	useDisclosurePrimitive,
	FlightIcon,
} from '@hashicorp/mds-react/components'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import { useSidebar } from 'components/sidebar/provider'
import { isLinkType } from '../../helpers/determine-item-type'
import type {
	SidebarLinkItemProps,
	SidebarMenuItemProps,
	SidebarProps,
	SidebarSection,
} from '../../types'
import type { ReactNode } from 'react'
import s from './style.module.css'

export default function DesktopSidebar({ sections }: SidebarProps) {
	return (
		<nav className={s.nav}>
			{sections.map((section, index) => (
				<Section {...section} key={`sidebar-section-${index}`} />
			))}
		</nav>
	)
}

function Section({ heading, sectionItems }: SidebarSection) {
	return (
		<>
			<div className={s.separator} role="separator" />
			{heading && (
				<div className={s.heading}>
					<Text.DisplayExpressive size="200" weight="bold">
						{heading}
					</Text.DisplayExpressive>
				</div>
			)}
			{sectionItems.map((item, index) =>
				isLinkType(item) ? (
					<TopLevelLink item={item} key={`sidebarItem${index}`} />
				) : (
					<MenuItem {...item} key={`sidebarItem${index}`} />
				),
			)}
		</>
	)
}

function TopLevelLink({ item }: { item: SidebarLinkItemProps }) {
	const { activeLink } = useSidebar()

	return (
		<>
			<LinkItem href={item.href}>
				<Text.Body size="300" weight="medium" color="strong">
					{item.text}
				</Text.Body>
			</LinkItem>
			{item.anchors && activeLink?.href === item.href ? (
				<ul className={s.anchors}>
					{item.anchors.map(({ href, text }) => (
						<li key={text} className={s.anchorItem}>
							<AnchorLink href={href} className={s.anchor}>
								<Text.Body size="200" color="faint">
									{text}
								</Text.Body>
							</AnchorLink>
						</li>
					))}
				</ul>
			) : null}
		</>
	)
}

function LinkItem({
	href,
	children,
}: Pick<SidebarLinkItemProps, 'href'> & { children: ReactNode }) {
	const { activeLink } = useSidebar()

	return (
		<div
			className={classNames(s.navListItem, {
				[s.isActive]: activeLink?.href === href,
			})}
		>
			<Link
				href={parseUrl(href).href}
				aria-current={activeLink?.href === href ? 'page' : undefined}
			>
				<div className={s.itemInner}>{children}</div>
			</Link>
		</div>
	)
}

function MenuItem({ title, routes }: SidebarMenuItemProps) {
	const { activeMenu } = useSidebar()

	return (
		<DisclosurePrimitive.Provider
			trackedIsOpen={activeMenu?.title === title}
			className={s.menuItem}
		>
			<DisclosureContent title={title} routes={routes} />
		</DisclosurePrimitive.Provider>
	)
}

function DisclosureContent({ title, routes }: SidebarMenuItemProps) {
	const { isOpen, onClickToggle } = useDisclosurePrimitive()

	return (
		<>
			<button
				aria-expanded={isOpen}
				className={classNames(s.toggleButton, { [s.isOpen]: isOpen })}
				type="button"
				onClick={onClickToggle}
			>
				<Text.Body size="300" weight="medium" color="strong">
					{title}
				</Text.Body>
				<FlightIcon name="chevron-down" />
			</button>
			<DisclosurePrimitive.Content>
				<div className={s.subMenu}>
					{routes.map((route) => (
						<LinkItem href={route.href} key={route.text}>
							<Text.Body size="200" color="strong" weight="medium">
								{route.text}
							</Text.Body>
						</LinkItem>
					))}
				</div>
			</DisclosurePrimitive.Content>
		</>
	)
}
